'use client'

import React, { FC, ReactElement } from 'react'
import styles from './WalletDescription.module.css'
import WalletDetailCard from '../wallet-detail-card'
import WalletCoinCard from '../wallet-coin-card'
import useWalletBalance from '@/utils/hooks/useWalletBalance'
import Loading from '@/components/circle-loading'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import classNames from 'classnames'
import { getSatoshiPriceUSD } from '@/utils/hooks/useSatoshiPriceUSD'
import useSatoshiPrice from '@/utils/hooks/useSatoshiPrice'
import BRC20Manager from '../brc20-manager'
import useBrc20Balance from '@/utils/hooks/useBrc20Balance'
import useTokenPrices from '@/utils/hooks/useTokenPrices'
import isRune from '@/utils/is-rune'
import event from '@/utils/event'
import isAlkanes from '@/utils/is-alkanes'
import useSWR from 'swr'
import { fetcher } from '@/utils/fetcher'
import { API_URL, COLLECTIONS_VERSION } from '@/utils/constants'

import CollectionDetail from '@/components/search-input/pages/collection-detail'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

type Tab = 'my-inscriptions' | 'wallet-history' | 'brc20' | 'utxos'

const WalletDescription: FC<{
	openTab: (tab: Tab, ticker?: string) => void
	activeOffers: number
}> = (props): ReactElement => {
	const [viewAll, setViewAll] = React.useState(false)
	const { data, isLoading } = useWalletBalance()
	const { exchangeRate, isLoading: isLoadingExchangeRate } = useExchangeRate()

	const [ticker, _setTicker] = React.useState('')
	const [tickerType, _setTickerType] = React.useState('')
	const [slug, _setSlug] = React.useState('')
	const [brc20Open, setBrc20Open] = React.useState(false)
	const [brc20Tab, setBrc20Tab] = React.useState('list')
	const toggleOpen = React.useCallback(() => setBrc20Open(!brc20Open), [brc20Open])

	const { data: brc20Data, isLoading: isLoadingBrc20 } = useBrc20Balance()
	const { data: tokenPrices } = useTokenPrices()

	// Fetch collections with volume data for sorting
	const { data: collectionsData } = useSWR(
		`${API_URL}/${COLLECTIONS_VERSION}?limit=500&order=VolumeTotalDesc`,
		fetcher
	)

	// Create a map of slug -> volume_all_time for fast lookup
	const volumeMap = React.useMemo(() => {
		const map = new Map<string, number>()
		if (collectionsData?.collections) {
			collectionsData.collections.forEach((c: any) => {
				if (c?.slug && c?.volume_all_time) {
					map.set(c.slug, c.volume_all_time)
				}
			})
		}
		return map
	}, [collectionsData])

	const renderCoinCard = (coin: any, index: any) => (
		<WalletCoinCard
			key={index}
			ticker={coin?.ticker}
			tickerIcon={coin.tickerIcon}
			balanceCoin={coin?.overall_balance}
			balanceUsd={coin?.balanceUSD}
			collection={coin.collection}
			description="Manage token"
			onClick={() => {
				const slug = coin?.collection?.slug
				event.emit('command', {
					command: {
						featured: false,
						component: CollectionDetail,
						name: isAlkanes(slug) ? coin.ticker : slug?.split('-')?.[1],
						args: { slug },
						heading: 'Collections'
					}
				})
			}}
			type={coin.type}
			params={coin}
		/>
	)

	const coins = (brc20Data || [])
		?.map((c) => {
			let satoshiPrice = 0
			let balanceUSD = '0.00'
			const collection = (tokenPrices || []).find((e) => e.slug === c?.collection?.slug)

			if (collection?.floor_price_per && c?.overall_balance) {
				satoshiPrice = collection?.floor_price_per * c.overall_balance
				balanceUSD = getSatoshiPriceUSD(satoshiPrice, exchangeRate)
			}
			if (collection?.floor_price_per && isRune(collection?.slug)) {
				satoshiPrice = collection?.floor_price_per * c.amount
				balanceUSD = getSatoshiPriceUSD(satoshiPrice, exchangeRate)
			}

			// Get volume from the collections API data
			const volumeAllTime = volumeMap.get(c?.collection?.slug) || 0

			return { ...c, balanceUSD, satoshiPrice, volumeAllTime }
		})
		?.sort((a, b) => b.volumeAllTime - a.volumeAllTime)
		?.map(renderCoinCard)

	const frozenBalance = useSatoshiPrice(data?.frozen_balance)

	return (
		<div className={styles.root}>
			<div className={styles.walletDetailContainer}>
				<div className={styles.walletDetailRow}>
					<WalletDetailCard
						tooltip="Frozen balance are coins that are not able to be used due to being active in offers, inscriptions, or contain rare sats. Cancel your offers or send your inscriptions with lower outputs to make these coins spendable again."
						detailName="FROZEN BALANCE"
						value={frozenBalance}
						isLoading={isLoading}
						onClick={() => {}}
					/>
				</div>
				<div className={styles.walletDetailRow}>
					<WalletDetailCard
						tooltip="UTXOs are needed to make multiple transactions quickly. Send more Bitcoin to your wallet to increase your UTXOs. For example, if you want to make ten $100 transactions, send ten transactions to your wallet in the amount of $100 each."
						detailName="UTXOS"
						value={data?.utxo_count}
						isLoading={isLoading}
						onClick={() => props.openTab('utxos')}
					/>
				</div>
				<div className={styles.walletDetailRow}>
					<WalletDetailCard
						detailName="INSCRIPTIONS"
						value={data?.inscriptions?.length}
						isLoading={isLoading}
						onClick={() => props.openTab('my-inscriptions')}
					/>
					<WalletDetailCard
						detailName="TOKENS"
						value={coins?.length}
						isLoading={isLoading}
						onClick={() => props.openTab('brc20')}
					/>
				</div>
			</div>
			<div className={styles.coinsInWallet}>
				{isLoadingBrc20 || isLoadingExchangeRate ? (
					<Loading />
				) : viewAll ? (
					coins
				) : (
					coins?.slice(0, 3)
				)}
				{coins?.length > 3 && (
					<div
						className={classNames(styles.viewAll, 'text-sm', 'medium')}
						onClick={() => setViewAll(!viewAll)}
						role="button"
						tabIndex={0}
						onKeyDown={onKeyboardClick(() => setViewAll(!viewAll))}
					>
						{viewAll ? 'View less' : 'View all'}
					</div>
				)}
			</div>
			{brc20Open && (
				<BRC20Manager
					tab={brc20Tab}
					slug={slug}
					setTab={setBrc20Tab}
					ticker={ticker}
					tickerType={tickerType}
					onComplete={toggleOpen}
					open={brc20Open}
					toggleOpen={toggleOpen}
				/>
			)}
		</div>
	)
}
export default WalletDescription
